import { FC, RefObject, useRef } from 'react';
import {
  CreateDealDocumentType,
  CreateSellerRequestValues,
  RequestDocumentType,
} from '../types';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { DidoxDocumentsModalRef } from '@/shared/modals/didox-documents-modal/types';
import { DidoxDocumentWidget } from '@/shared/didox-document-widget';
import { Buyer } from '../../../app/api/types';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { DidoxDocument } from '@/entities/didox/types';
import { UseFormGetValues } from 'react-hook-form';

interface Props {
  type: CreateDealDocumentType;
  didoxModalRef: RefObject<DidoxDocumentsModalRef>;
  onMultipleChange: (value: DidoxDocument[]) => void;
  getValues: UseFormGetValues<CreateSellerRequestValues>;
  required?: boolean;
  invalid?: boolean;
  value?: RequestDocumentType;
  onChange?: (value: RequestDocumentType) => void;
  partner: Buyer | null | string;
  onDelete?: () => void;
  disabled?: boolean;
  additionTypes?: CreateDealDocumentType[];
  disablePcButton?: boolean;
  isInternationalFactoring?: boolean;
}

const ALLOWED_PDF_SIZE = 10 * 1024 * 1024;

export const CreateDealChoseDocument: FC<Props> = ({
  type,
  required,
  didoxModalRef,
  invalid,
  value,
  onChange,
  partner,
  onDelete,
  disabled,
  additionTypes,
  onMultipleChange,
  disablePcButton,
  isInternationalFactoring,
}) => {
  const { t } = useTranslation('createDealForm');
  const { showNotification } = useNotification();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChoseDocOnDidox = async () => {
    const doctype = additionTypes ? [type, ...additionTypes] : type;
    const isMulti = Array.isArray(doctype)
      ? doctype.includes('002') || doctype.includes('008')
      : doctype === '002';
    const documents = await didoxModalRef.current?.open(doctype, isMulti);

    if (documents?.length) {
      if (documents.length === 1) {
        onChange?.(documents[0]);
      } else if (documents.length > 1) {
        if (value) {
          onMultipleChange(documents);
        } else {
          let docs = [...documents];
          if (onChange) {
            onChange?.(docs?.[0]);
            docs = docs.slice(1);
          }

          onMultipleChange(docs);
        }
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (file.size > ALLOWED_PDF_SIZE) {
      return showNotification(
        t('errors:max_size_error', { size: ALLOWED_PDF_SIZE / 1024 / 1024 }),
        'error'
      );
    }

    if (file.type !== 'application/pdf') {
      return showNotification(t('errors:only_pdf_allowed'), 'error');
    }

    if (onChange) {
      onChange({ value: file });
    }
  };

  const handleClickOnUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isDidoxBlockedByInternational =
    isInternationalFactoring && type !== '075';

  const isPcUploadDisabled =
    disabled || !partner || (disablePcButton && !isInternationalFactoring);
  const isDidoxDisabled = disabled || !partner || isDidoxBlockedByInternational;

  const pcButtonTooltipTitle = () => {
    if (disablePcButton && !isInternationalFactoring) {
      return t('disabled_pc_button_for_invoice');
    }
    if (!partner) {
      return t('common:choose_partner_first');
    }
    return '';
  };

  const didoxButtonTooltipTitle = () => {
    if (isDidoxBlockedByInternational) {
      return t('disabled_didox_button_for_international_factoring');
    }
    if (!partner) {
      return t('common:choose_partner_first');
    }
    return '';
  };

  if (value) {
    return (
      <DidoxDocumentWidget
        value={value}
        showPreview
        showHtml={false}
        onDelete={() => {
          if (onDelete) {
            onDelete();
          } else if (onChange) {
            onChange(null);
          }
        }}
      />
    );
  }

  return (
    <Stack direction='row' spacing={1}>
      <Box
        sx={{
          flex: 1,
          borderWidth: 1,
          borderColor: invalid ? 'error.light' : 'rgba(230, 241, 255, 1)',
          borderStyle: 'solid',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          px: 2,
        }}
      >
        <UIFormLabel required={required} sx={{ margin: 0 }}>
          {t(
            `chose_document_options.label.${
              type === '002' && isInternationalFactoring
                ? '002_international'
                : type
            }`
          )}
          :
        </UIFormLabel>
      </Box>

      <Tooltip title={pcButtonTooltipTitle()}>
        <Box component='span'>
          <Button
            color='inherit'
            disabled={isPcUploadDisabled}
            onClick={handleClickOnUpload}
            sx={{
              height: '100%',
            }}
          >
            {t('common:upload_from_pc')}
          </Button>
        </Box>
      </Tooltip>
      <Tooltip title={didoxButtonTooltipTitle()}>
        <Box component='span'>
          <Button
            color='secondary'
            disabled={isDidoxDisabled}
            sx={{
              width: 220,
            }}
            onClick={handleChoseDocOnDidox}
          >
            {t(
              `chose_document_options.button.${type === '002' && isInternationalFactoring ? '002_international' : type}`
            )}
          </Button>
        </Box>
      </Tooltip>

      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept='.pdf'
        onChange={handleFileChange}
      />
    </Stack>
  );
};
